import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Container,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  FaBuilding,
  FaEdit,
  FaSave,
  FaTimes,
  FaGlobe,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUsers,
  FaCalendarAlt,
  FaStar,
  FaCheckCircle,
  FaIndustry,
  FaChartLine,
  FaEyeSlash,
  FaLink,
  FaChartBar,
} from "react-icons/fa";
import { RootState, useAppDispatch } from "../../store";
import { Icon } from "../../components/common/Icon";
import CompanyAvatar from "../../components/common/CompanyAvatar";
import LogoUploadField from "../../components/common/LogoUploadField";
import companiesService, { Company, CompanyUpdate } from "../../features/companies/companiesService";
import { isCompanyOwner } from "../../utils/permissions";
import CompanyFormModal from "../companies/components/CompanyFormModal";
import { resolveLogoUrl } from "../../utils/urlUtils";

interface AgencyData extends Company {
  founded_year?: number;
  employee_count?: number;
  banner_url?: string;
  social_media?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
  };
  services?: string[];
  achievements?: string[];
}

const AgencyProfile: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dispatch = useAppDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);

  const [agencyData, setAgencyData] = useState<AgencyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isNewCompany, setIsNewCompany] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [formData, setFormData] = useState<Partial<AgencyData>>({});
  const [actualLogoUrl, setActualLogoUrl] = useState<string>("");


  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const desiredType = params.get("type"); // 'agency' or 'single'

  useEffect(() => {
    const fetchAgencyData = async () => {
      try {
        setIsLoading(true);
        const data = isCompanyOwner(user)
          ? await companiesService.getMyCompany().catch(() => null)
          : await companiesService.getMyAgency().catch(() => null);

        // Eğer kayıt sonrası ilk giriş ve henüz company yoksa, boş form ile başlat
        if (!data) {
          const initial: AgencyData = {
            id: -1 as any,
            company_name: "",
            company_type: (desiredType as any) || (isCompanyOwner(user) ? "single" : "agency"),
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            managed_companies: [],
            managing_agencies: [],
          } as unknown as AgencyData;
          setAgencyData(initial);
          setFormData(initial);
          setIsEditing(true);
          setIsNewCompany(true);
          setShowCreateModal(true);
          return;
        }

        // API'den gelen veriyi AgencyData formatına dönüştür
        const agencyData: AgencyData = {
          ...data,
          founded_year: data.establishment_date ? new Date(data.establishment_date).getFullYear() : undefined,
          employee_count: data.company_size ? parseInt(data.company_size) : undefined,
          banner_url: data.logo_url,
          social_media: {},
          services: [],
          achievements: [],
        };

        setAgencyData(agencyData);
        setFormData(agencyData);
      } catch (error) {
        console.error("Error fetching agency data:", error);
        setError("Failed to load agency profile. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgencyData();
  }, [location.search, desiredType, user]);

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(agencyData || {});
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const wasNewCompany = Boolean(agencyData && (agencyData.id === (-1 as any)));
      // API'ye gönderilecek veriyi hazırla
      const updateData: CompanyUpdate = {
        company_name: formData.company_name,
        description: formData.description,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        postal_code: formData.postal_code,
        industry: formData.industry,
        establishment_date: formData.establishment_date || undefined,
        logo_url: actualLogoUrl || formData.logo_url,
        company_size: formData.company_size,
        is_active: formData.is_active,
      };
      // Eğer henüz company yoksa önce create et
      let updatedData;
      if (agencyData && agencyData.id === (-1 as any)) {
        const createPayload = {
          company_name: updateData.company_name || "",
          company_type: (desiredType as any) || (isCompanyOwner(user) ? "single" : "agency"),
          industry: updateData.industry,
          establishment_date: updateData.establishment_date,
          description: updateData.description,
          website: updateData.website,
          email: updateData.email,
          phone: updateData.phone,
          address: updateData.address,
          city: updateData.city,
          country: updateData.country,
          postal_code: updateData.postal_code,
          tax_id: undefined,
          company_size: updateData.company_size,
          annual_revenue: undefined,
          logo_url: updateData.logo_url,
          is_active: updateData.is_active,
        };
        // create and bind is handled by backend (for owner roles)
        updatedData = await companiesService.createCompany(createPayload as any);
      } else {
        updatedData = isCompanyOwner(user)
          ? await companiesService.updateMyCompany(updateData)
          : await companiesService.updateMyAgency(updateData);
      }

      // Güncellenmiş veriyi state'e set et
      const updatedAgencyData: AgencyData = {
        ...updatedData,
        founded_year: updatedData.establishment_date ? new Date(updatedData.establishment_date).getFullYear() : undefined,
        employee_count: updatedData.company_size ? parseInt(updatedData.company_size) : undefined,
        banner_url: updatedData.logo_url,
        social_media: agencyData?.social_media || {},
        services: agencyData?.services || [],
        achievements: agencyData?.achievements || [],
      };

      setAgencyData(updatedAgencyData);
      setFormData(updatedAgencyData);
      setIsEditing(false);
      setIsNewCompany(false);
      setSuccess(wasNewCompany ? "Company created successfully!" : "Company updated successfully!");

      // clear setup required flag once company exists
      localStorage.removeItem('companySetupRequired');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error updating agency:", err);
      setError("Failed to update agency profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof AgencyData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge bg="success" className="status-badge-modern success">
        <Icon icon={FaCheckCircle} className="me-1" />
        Active
      </Badge>
    ) : (
      <Badge bg="secondary" className="status-badge-modern secondary">
        <Icon icon={FaEyeSlash} className="me-1" />
        Inactive
      </Badge>
    );
  };

  const renderFormField = (
    label: string,
    field: keyof AgencyData,
    type: string = "text",
    placeholder?: string,
    icon?: any,
    options?: { value: string; label: string }[]
  ) => {
    const value = formData[field];

    return (
      <Form.Group className="mb-3">
        <Form.Label className="form-label">
          {icon && <Icon icon={icon} className="me-2" />}
          {label}
        </Form.Label>
        {isEditing ? (
          type === "textarea" ? (
            <Form.Control
              as="textarea"
              rows={4}
              value={String(value || "")}
              onChange={(e) => handleInputChange(field, e.target.value)}
              placeholder={placeholder}
            />
          ) : type === "select" && options ? (
            <Form.Select
              value={String(value || "")}
              onChange={(e) => handleInputChange(field, e.target.value)}
            >
              <option value="">Select {label.toLowerCase()}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          ) : type === "switch" ? (
            <Form.Check
              type="switch"
              id={`${field}-switch`}
              label={Boolean(value) ? "Active" : "Inactive"}
              checked={Boolean(value)}
              onChange={(e) => handleInputChange(field, e.target.checked)}
            />
          ) : (
            <Form.Control
              type={type}
              value={String(value || "")}
              onChange={(e) => handleInputChange(field, e.target.value)}
              placeholder={placeholder}
            />
          )
        ) : (
          <div className="form-display border rounded p-2 bg-light text-dark">
            {value ? String(value) : "Not provided"}
          </div>
        )}
      </Form.Group>
    );
  };

  if (isLoading) {
    return (
      <div className="agency-page-layout">
        <div className="modern-loading-state">
          <div className="loading-spinner">
            <Spinner animation="border" variant="primary" />
          </div>
          <p className="loading-title">Loading agency profile...</p>
        </div>
      </div>
    );
  }

  if (!agencyData) {
    return (
      <div className="agency-page-layout">
        <div className="modern-empty-state">
          <Icon icon={FaBuilding} size="3x" className="empty-icon-modern" />
          <h4 className="empty-title-modern">No Agency Found</h4>
          <p className="empty-description-modern">Agency profile is not configured yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agency-page-layout">
      {/* Header */}
      <div className="modern-header">
        <Container fluid>
          <div className="header-content">
            <div className="agency-profile-info">
              <div className="agency-logo-wrapper">
                <CompanyAvatar
                  companyName={agencyData.company_name}
                  logoUrl={agencyData.logo_url ? resolveLogoUrl(agencyData.logo_url) : undefined}
                  size="lg"
                  className="agency-profile-logo"
                />
              </div>
              <div className="agency-details">
                <h1 className="header-title">{agencyData.company_name}</h1>
                <p className="header-subtitle mb-2">{agencyData.industry}</p>
                <div className="agency-meta">
                  {getStatusBadge(agencyData.is_active)}
                  {agencyData.founded_year && (
                    <span className="meta-item">
                      <Icon icon={FaCalendarAlt} />
                      Founded {agencyData.founded_year}
                    </span>
                  )}
                  {agencyData.employee_count && (
                    <span className="meta-item">
                      <Icon icon={FaUsers} />
                      {agencyData.employee_count} Employees
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="header-actions mt-3 mt-md-0">
              {!isEditing ? (
                <Button
                  variant="primary"
                  onClick={handleEdit}
                  className="btn-modern btn-primary"
                >
                  <Icon icon={FaEdit} />
                  Edit Company
                </Button>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    variant="cancel"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="btn-modern btn-cancel"
                  >
                    <Icon icon={FaTimes} />
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-modern btn-primary"
                  >
                    {isSaving ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        {isNewCompany ? "Saving..." : "Saving..."}
                      </>
                    ) : (
                      <>
                        <Icon icon={FaSave} />
                        {isNewCompany ? "Save Company" : "Save Changes"}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* Alerts */}
      {error && (
        <Container fluid className="mb-4">
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        </Container>
      )}
      {success && (
        <Container fluid className="mb-4">
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Container>
      )}

      {/* Content */}
      <Container fluid>
        <Row>
          <Col lg={8}>
            {/* Company Details Card */}
            <Card className="profile-card">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaBuilding} />
                  Company Information
                </h5>
                <Row>
                  <Col md={6}>
                    {renderFormField("Company Name", "company_name", "text", "Enter company name", FaBuilding)}
                  </Col>
                  <Col md={6}>
                    {renderFormField("Industry", "industry", "text", "Enter industry", FaIndustry)}
                  </Col>
                </Row>
                {renderFormField("Description", "description", "textarea", "Enter company description")}
              </Card.Body>
            </Card>

            {/* Contact Details Card */}
            <Card className="profile-card">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaEnvelope} />
                  Contact Information
                </h5>
                <Row>
                  <Col md={6}>
                    {renderFormField("Email Address", "email", "email", "Enter email address", FaEnvelope)}
                  </Col>
                  <Col md={6}>
                    {renderFormField("Phone Number", "phone", "tel", "Enter phone number", FaPhone)}
                  </Col>
                </Row>
                {renderFormField("Website URL", "website", "url", "https://example.com", FaGlobe)}
              </Card.Body>
            </Card>

            {/* Address Information Card */}
            <Card className="profile-card">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaMapMarkerAlt} />
                  Address Information
                </h5>
                {renderFormField("Street Address", "address", "text", "Enter street address", FaMapMarkerAlt)}
                <Row>
                  <Col md={4}>
                    {renderFormField("City", "city", "text", "Enter city")}
                  </Col>
                  <Col md={4}>
                    {renderFormField("Country", "country", "text", "Enter country")}
                  </Col>
                  <Col md={4}>
                    {renderFormField("Postal Code", "postal_code", "text", "Enter postal code")}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Additional Information Card */}
            <Card className="profile-card">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaChartBar} />
                  Additional Information
                </h5>
                <Row>
                  <Col md={6}>
                    {renderFormField("Establishment Date", "establishment_date", "date", "", FaCalendarAlt)}
                  </Col>
                  <Col md={6}>
                    {renderFormField("Number of Employees", "company_size", "text", "e.g., 10-50 employees", FaUsers)}
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">
                        <Icon icon={FaLink} className="me-2" />
                        Company Logo
                      </Form.Label>
                      {isEditing ? (
                        <LogoUploadField
                          companyId={agencyData?.id !== -1 ? agencyData?.id : undefined}
                          value={formData.logo_url || ""}
                          onChange={(url) => handleInputChange("logo_url", url)}
                          onLogoUrlChange={setActualLogoUrl}
                          label=""
                          helperText="Upload a logo file or enter a URL"
                        />
                      ) : (
                        <div className="form-display border rounded p-2 bg-light text-dark">
                          {formData.logo_url ? (
                            <div className="d-flex align-items-center gap-3">
                              <img
                                src={resolveLogoUrl(formData.logo_url)}
                                alt="Company Logo"
                                style={{ maxWidth: "80px", maxHeight: "80px", objectFit: "contain" }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                              <span className="text-break">{formData.logo_url}</span>
                            </div>
                          ) : (
                            "Not provided"
                          )}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">
                        <Icon icon={FaCheckCircle} className="me-2" />
                        Account Status
                      </Form.Label>
                      {isEditing ? (
                        <Form.Check
                          type="switch"
                          id="status-switch"
                          label={formData.is_active ? "Active" : "Inactive"}
                          checked={formData.is_active || false}
                          onChange={(e) => handleInputChange("is_active", e.target.checked)}
                        />
                      ) : (
                        <div className="d-flex align-items-center">
                          {getStatusBadge(agencyData.is_active)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Agency Status Card */}
            <Card className="profile-card">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaStar} />
                  Account Status
                </h5>
                <div className="status-info">
                  <div className="status-item">
                    <label>Current Status</label>
                    {getStatusBadge(agencyData.is_active)}
                  </div>
                  <div className="status-item">
                    <label>Last Updated</label>
                    <p>{new Date(agencyData.updated_at).toLocaleDateString()}</p>
                  </div>
                  <div className="status-item">
                    <label>Created Date</label>
                    <p>{new Date(agencyData.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>


          </Col>
        </Row>
      </Container>

      {/* Auto-open create modal when user has no company and came with type hint */}
      <CompanyFormModal
        show={showCreateModal}
        onHide={async () => {
          setShowCreateModal(false);
          try {
            const refreshed = isCompanyOwner(user)
              ? await companiesService.getMyCompany()
              : await companiesService.getMyAgency();
            if (refreshed) {
              const updatedAgencyData: AgencyData = {
                ...refreshed,
                founded_year: refreshed.establishment_date ? new Date(refreshed.establishment_date).getFullYear() : undefined,
                employee_count: refreshed.company_size ? parseInt(refreshed.company_size) : undefined,
                banner_url: refreshed.logo_url,
                social_media: {},
                services: [],
                achievements: [],
              } as AgencyData;
              setAgencyData(updatedAgencyData);
              setFormData(updatedAgencyData);
              setIsEditing(false);
              setIsNewCompany(false);
              localStorage.removeItem('companySetupRequired');
            }
          } catch (e) {
            // stay on page; user might have cancelled
          }
        }}
        initialCompanyType={(desiredType as any) || (isCompanyOwner(user) ? "single" : "agency")}
      />
    </div>
  );
};

export default AgencyProfile;
